import { useCallback, useMemo, useState } from 'react';
import constate from '../../common/lib/constate';

const initialMusicVolumeValue = 50;
const initialVideoVolumeValue = 100;

function useBackgroundMusic() {
  const [isBackgroundMusicOpened, setIsBackgroundMusicOpened] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [availableMusic, setAvailableMusic] = useState([]);

  const [playerPlayedMusic, setPlayerPlayedMusic] = useState(null);
  const [previewPlayedMusic, setPreviewPlayedMusic] = useState(null);

  const [musicVolume, setMusicVolume] = useState(initialMusicVolumeValue);
  const [videoVolume, setVideoVolume] = useState(initialVideoVolumeValue);

  const [isApplyAllTrackEnabled, setIsApplyAllTrackEnabled] = useState(null);
  const [isApplyAllVolumeEnabled, setIsApplyAllVolumeEnabled] = useState(null);

  const initBackgroundMusic = useCallback((sameTrack = null, sameVolume = null) => {
    if (isApplyAllTrackEnabled == null) {
      setIsApplyAllTrackEnabled(sameTrack == null ? true : sameTrack);
    }
    if (isApplyAllVolumeEnabled == null) {
      setIsApplyAllVolumeEnabled(sameVolume == null ? true : sameVolume);
    }
  }, [isApplyAllTrackEnabled, isApplyAllVolumeEnabled])

  const enableBackgroundMusic = useCallback(() => {
    setIsBackgroundMusicOpened(true);
  }, []);

  const disableBackgroundMusic = useCallback(() => {
    setIsBackgroundMusicOpened(false);
  }, []);

  const changeMusicVolume = useCallback(value => {
    setMusicVolume(value ?? initialMusicVolumeValue);
  }, []);

  const changeVideoVolume = useCallback(value => {
    setVideoVolume(value ?? initialVideoVolumeValue);
  }, []);

  const chooseBackgroundMusic = useCallback(track => {
    setSelectedMusic(track);
  }, []);

  const handlePlayerPlayedMusic = useCallback(id => {
    setPlayerPlayedMusic(id);
  }, []);

  const handlePreviewPlayedMusic = useCallback(id => {
    setPreviewPlayedMusic(id);
  }, []);

  const toggleApplyAllTrack = useCallback(() => {
    setIsApplyAllTrackEnabled(prevState => !prevState);
  }, [setIsApplyAllTrackEnabled]);

  const toggleApplyAllVolume = useCallback(() => {
    setIsApplyAllVolumeEnabled(prevState => !prevState);
  }, [setIsApplyAllVolumeEnabled]);

  const resetBackgroundMusicContext = useCallback(() => {
    changeMusicVolume(initialMusicVolumeValue);
    changeVideoVolume(initialVideoVolumeValue);
    chooseBackgroundMusic(null);
    disableBackgroundMusic();
    setIsApplyAllTrackEnabled(null);
    setIsApplyAllVolumeEnabled(null);
  }, []);

  return useMemo(
    () => ({
      isBackgroundMusicOpened,
      availableMusic,
      selectedMusic,
      musicVolume,
      videoVolume,
      playerPlayedMusic,
      previewPlayedMusic,
      isApplyAllTrackEnabled,
      isApplyAllVolumeEnabled,
      setAvailableMusic,
      enableBackgroundMusic,
      disableBackgroundMusic,
      chooseBackgroundMusic,
      changeMusicVolume,
      changeVideoVolume,
      handlePlayerPlayedMusic,
      handlePreviewPlayedMusic,
      toggleApplyAllTrack,
      toggleApplyAllVolume,
      resetBackgroundMusicContext,
      initBackgroundMusic,
    }),
    [
      isBackgroundMusicOpened,
      availableMusic,
      selectedMusic,
      musicVolume,
      videoVolume,
      playerPlayedMusic,
      previewPlayedMusic,
      isApplyAllTrackEnabled,
      isApplyAllVolumeEnabled,
      setAvailableMusic,
      enableBackgroundMusic,
      disableBackgroundMusic,
      chooseBackgroundMusic,
      changeMusicVolume,
      changeVideoVolume,
      handlePlayerPlayedMusic,
      handlePreviewPlayedMusic,
      toggleApplyAllTrack,
      toggleApplyAllVolume,
      resetBackgroundMusicContext,
      initBackgroundMusic,
    ]
  );
}

export const useBackgroundMusicContext = constate(useBackgroundMusic);

const BackgroundMusicContext = {
  Provider: useBackgroundMusicContext.Provider,
};

export default BackgroundMusicContext;
